import { BackstockLedgerResponse, BackstockLedgerRow } from "features/audits/types";

const parseNumber = (value: unknown): number => {
  if (value == null || value === "") {
    return 0;
  }

  const nextValue = typeof value === "number" ? value : Number(value);
  return Number.isFinite(nextValue) ? nextValue : 0;
};

export interface StoreBackstockLedgerDayModel {
  reportDate: string;
  openingPacks: number;
  openingTickets: number;
  activatedToBackstockPacks: number;
  activatedToBackstockTickets: number;
  movedFromCounterPacks: number;
  movedFromCounterTickets: number;
  returnedToCounterPacks: number;
  returnedToCounterTickets: number;
  returnedToLotteryPacks: number;
  returnedToLotteryTickets: number;
  inventoryDepletionPacks: number;
  inventoryDepletionTickets: number;
  closingPacks: number;
  closingTickets: number;
  resolvedAfterRangePacks: number;
  resolvedAfterRangeTickets: number;
  counterOpeningPacks: number;
  counterOpeningTickets: number;
  counterActivatedPacks: number;
  counterActivatedTickets: number;
  counterFromBackstockPacks: number;
  counterFromBackstockTickets: number;
  counterToBackstockPacks: number;
  counterToBackstockTickets: number;
  counterSoldOutPacks: number;
  counterSoldOutTickets: number;
  counterClosingPacks: number;
  counterClosingTickets: number;
  counterExpectedSalesTickets: number;
  hasVendingInventory: boolean;
  vendingOpeningPacks: number;
  vendingOpeningTickets: number;
  vendingActivatedPacks: number;
  vendingActivatedTickets: number;
  vendingFromBackstockPacks: number;
  vendingFromBackstockTickets: number;
  vendingToBackstockPacks: number;
  vendingToBackstockTickets: number;
  vendingSoldOutPacks: number;
  vendingSoldOutTickets: number;
  vendingClosingPacks: number;
  vendingClosingTickets: number;
  vendingExpectedSalesTickets: number;
}

const mapRow = (row: BackstockLedgerRow): StoreBackstockLedgerDayModel => ({
  reportDate: String(row.report_date ?? ""),
  openingPacks: parseNumber(row.opening_packs),
  openingTickets: parseNumber(row.opening_tickets),
  activatedToBackstockPacks: parseNumber(row.activated_to_backstock_packs),
  activatedToBackstockTickets: parseNumber(row.activated_to_backstock_tickets),
  movedFromCounterPacks: parseNumber(row.moved_from_counter_packs),
  movedFromCounterTickets: parseNumber(row.moved_from_counter_tickets),
  returnedToCounterPacks: parseNumber(row.returned_to_counter_packs),
  returnedToCounterTickets: parseNumber(row.returned_to_counter_tickets),
  returnedToLotteryPacks: parseNumber(row.returned_to_lottery_packs),
  returnedToLotteryTickets: parseNumber(row.returned_to_lottery_tickets),
  inventoryDepletionPacks: parseNumber(row.inventory_depletion_packs),
  inventoryDepletionTickets: parseNumber(row.inventory_depletion_tickets),
  closingPacks: parseNumber(row.closing_packs),
  closingTickets: parseNumber(row.closing_tickets),
  resolvedAfterRangePacks: parseNumber(row.resolved_after_range_packs),
  resolvedAfterRangeTickets: parseNumber(row.resolved_after_range_tickets),
  counterOpeningPacks: parseNumber(row.counter_opening_packs),
  counterOpeningTickets: parseNumber(row.counter_opening_tickets),
  counterActivatedPacks: parseNumber(row.counter_activated_packs),
  counterActivatedTickets: parseNumber(row.counter_activated_tickets),
  counterFromBackstockPacks: parseNumber(row.counter_from_backstock_packs),
  counterFromBackstockTickets: parseNumber(row.counter_from_backstock_tickets),
  counterToBackstockPacks: parseNumber(row.counter_to_backstock_packs),
  counterToBackstockTickets: parseNumber(row.counter_to_backstock_tickets),
  counterSoldOutPacks: parseNumber(row.counter_sold_out_packs),
  counterSoldOutTickets: parseNumber(row.counter_sold_out_tickets),
  counterClosingPacks: parseNumber(row.counter_closing_packs),
  counterClosingTickets: parseNumber(row.counter_closing_tickets),
  counterExpectedSalesTickets: parseNumber(row.counter_expected_sales_tickets),
  hasVendingInventory: Object.prototype.hasOwnProperty.call(row, "vending_opening_tickets"),
  vendingOpeningPacks: parseNumber(row.vending_opening_packs),
  vendingOpeningTickets: parseNumber(row.vending_opening_tickets),
  vendingActivatedPacks: parseNumber(row.vending_activated_packs),
  vendingActivatedTickets: parseNumber(row.vending_activated_tickets),
  vendingFromBackstockPacks: parseNumber(row.vending_from_backstock_packs),
  vendingFromBackstockTickets: parseNumber(row.vending_from_backstock_tickets),
  vendingToBackstockPacks: parseNumber(row.vending_to_backstock_packs),
  vendingToBackstockTickets: parseNumber(row.vending_to_backstock_tickets),
  vendingSoldOutPacks: parseNumber(row.vending_sold_out_packs),
  vendingSoldOutTickets: parseNumber(row.vending_sold_out_tickets),
  vendingClosingPacks: parseNumber(row.vending_closing_packs),
  vendingClosingTickets: parseNumber(row.vending_closing_tickets),
  vendingExpectedSalesTickets: parseNumber(row.vending_expected_sales_tickets),
});

export default class StoreBackstockLedgerModel {
  private _serverData: BackstockLedgerResponse;
  private _daily: StoreBackstockLedgerDayModel[] | null = null;
  private _overall: StoreBackstockLedgerDayModel | null = null;

  constructor(data: BackstockLedgerResponse) {
    this._serverData = data;
  }

  get storeId(): string {
    return this._serverData.store_id;
  }

  get storeName(): string | null {
    return this._serverData.store_name ?? null;
  }

  get storeTimezone(): string | null {
    return this._serverData.store_timezone ?? null;
  }

  get startDate(): string {
    return this._serverData.start_date;
  }

  get endDate(): string {
    return this._serverData.end_date;
  }

  get daily(): StoreBackstockLedgerDayModel[] {
    if (this._daily) {
      return this._daily;
    }

    this._daily = Array.isArray(this._serverData.daily) ? this._serverData.daily.map(mapRow) : [];
    return this._daily;
  }

  get overall(): StoreBackstockLedgerDayModel | null {
    if (this._overall) {
      return this._overall;
    }

    if (!this._serverData.overall) {
      return null;
    }

    this._overall = mapRow(this._serverData.overall);
    return this._overall;
  }

  getDay(reportDate: string): StoreBackstockLedgerDayModel | null {
    return this.daily.find((day) => day.reportDate === reportDate) ?? null;
  }

  static fromServerData(data: BackstockLedgerResponse): StoreBackstockLedgerModel {
    return new StoreBackstockLedgerModel(data);
  }
}
